import { Suspense, useState, useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createIDBPersister } from "@/lib/queryPersister";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { AdminGuard } from "@/components/auth/AdminGuard";
import { AdminOnlyGuard } from "@/components/auth/AdminOnlyGuard";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useTrackingCodes } from "@/hooks/useTrackingCodes";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import { VersionUpdateNotifier } from "@/components/VersionUpdateNotifier";
import { CartProvider, useCart } from "@/contexts/CartContext";
const CartSummaryDialog = lazyWithRetry(() => import("@/components/cart/CartSummaryDialog").then(m => ({ default: m.CartSummaryDialog })));

function DeferredCart() {
  const { isCartOpen } = useCart();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { if (isCartOpen) setMounted(true); }, [isCartOpen]);
  return mounted ? <Suspense fallback={isCartOpen ? <LoadingSpinner /> : null}><CartSummaryDialog /></Suspense> : null;
}
import Index from "./pages/Index";

// Lazy load pages
const Login = lazyWithRetry(() => import("./pages/Login"));
const Signup = lazyWithRetry(() => import("./pages/Signup"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const ConfirmSubscription = lazyWithRetry(() => import("./pages/ConfirmSubscription"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe"));
const EdmUnsubscribe = lazyWithRetry(() => import("./pages/EdmUnsubscribe"));
const Subscribe = lazyWithRetry(() => import("./pages/Subscribe"));
const ReferralSubscribe = lazyWithRetry(() => import("./pages/ReferralSubscribe"));
const QRScanner = lazyWithRetry(() => import("./pages/QRScanner"));
const ConfirmRestaurant = lazyWithRetry(() => import("./pages/ConfirmRestaurant"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const SitemapRedirect = lazyWithRetry(() => import("./pages/SitemapRedirect"));
const RestaurantLanding = lazyWithRetry(() => import("./pages/RestaurantLanding"));
const RestaurantPreview = lazyWithRetry(() => import("./pages/RestaurantPreview"));
const About = lazyWithRetry(() => import("./pages/About"));

// Admin pages
const Dashboard = lazyWithRetry(() => import("./pages/admin/Dashboard"));
const RestaurantList = lazyWithRetry(() => import("./pages/admin/RestaurantList"));
const CountryList = lazyWithRetry(() => import("./pages/admin/CountryList"));
const CityList = lazyWithRetry(() => import("./pages/admin/CityList"));
const DailyMenuList = lazyWithRetry(() => import("./pages/admin/DailyMenuList"));
const DailyMenuForm = lazyWithRetry(() => import("./pages/admin/DailyMenuForm"));
const Calendar = lazyWithRetry(() => import("./pages/admin/Calendar"));
const MenuTypeList = lazyWithRetry(() => import("./pages/admin/MenuTypeList"));
const DishList = lazyWithRetry(() => import("./pages/admin/DishList"));
const FoodTypeList = lazyWithRetry(() => import("./pages/admin/FoodTypeList"));
const CuisineTypeList = lazyWithRetry(() => import("./pages/admin/CuisineTypeList"));
const VoucherTypeList = lazyWithRetry(() => import("./pages/admin/VoucherTypeList"));
const UserList = lazyWithRetry(() => import("./pages/admin/UserList"));
const NewsletterList = lazyWithRetry(() => import("./pages/admin/NewsletterList"));
const EmailTemplates = lazyWithRetry(() => import("./pages/admin/EmailTemplates"));
const EmailTemplateForm = lazyWithRetry(() => import("./pages/admin/EmailTemplateForm"));
const TrackingCodes = lazyWithRetry(() => import("./pages/admin/TrackingCodes"));
const ContentElements = lazyWithRetry(() => import("./pages/admin/ContentElements"));
const ScheduledTasks = lazyWithRetry(() => import("./pages/admin/ScheduledTasks"));
const ScheduledTaskForm = lazyWithRetry(() => import("./pages/admin/ScheduledTaskForm"));
const Newsletters = lazyWithRetry(() => import("./pages/admin/Newsletters"));
const Logs = lazyWithRetry(() => import("./pages/admin/Logs"));
const BannerList = lazyWithRetry(() => import("./pages/admin/BannerList"));
const Gallery = lazyWithRetry(() => import("./pages/admin/Gallery"));
const PopupList = lazyWithRetry(() => import("./pages/admin/PopupList"));
const ServicePackageList = lazyWithRetry(() => import("./pages/admin/ServicePackageList"));
const Backup = lazyWithRetry(() => import("./pages/admin/Backup"));
const OrderList = lazyWithRetry(() => import("./pages/admin/OrderList"));
const DataAccess = lazyWithRetry(() => import("./pages/admin/DataAccess"));

const persister = createIDBPersister();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      retry: 1,
    },
    mutations: {
      onSuccess: () => {
        queryClient.invalidateQueries();
      },
    },
  },
});

const PageViewTracker = () => {
  usePageViewTracking();
  return null;
};

const AppContent = () => {
  useTrackingCodes();

  return (
    <CartProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <VersionUpdateNotifier />
      <BrowserRouter>
        <DeferredCart />
        <PageViewTracker />
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner /></div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/confirm-subscription" element={<ConfirmSubscription />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/edm-unsubscribe" element={<EdmUnsubscribe />} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/subscribe/ref/:code" element={<ReferralSubscribe />} />
            <Route path="/confirm-restaurant" element={<ConfirmRestaurant />} />
            <Route path="/qr-scanner" element={<AuthGuard><QRScanner /></AuthGuard>} />
            <Route path="/adatvedelem" element={<Privacy />} />
            <Route path="/etteremnek" element={<RestaurantLanding />} />
            <Route path="/rolunk" element={<About />} />
            <Route path="/elonezet/:token" element={<RestaurantPreview />} />
            <Route path="/admin" element={<AuthGuard><AdminGuard><Dashboard /></AdminGuard></AuthGuard>} />
            <Route path="/admin/naptar" element={<AuthGuard><AdminGuard><Calendar /></AdminGuard></AuthGuard>} />
            <Route path="/admin/ettermek" element={<AuthGuard><AdminGuard><RestaurantList /></AdminGuard></AuthGuard>} />
            <Route path="/admin/menu" element={<AuthGuard><AdminGuard><DailyMenuList /></AdminGuard></AuthGuard>} />
            <Route path="/admin/menu/uj" element={<AuthGuard><AdminGuard><DailyMenuForm /></AdminGuard></AuthGuard>} />
            <Route path="/admin/menu/:id" element={<AuthGuard><AdminGuard><DailyMenuForm /></AdminGuard></AuthGuard>} />
            <Route path="/admin/orszagok" element={<AuthGuard><AdminOnlyGuard><CountryList /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/telepulesek" element={<AuthGuard><AdminOnlyGuard><CityList /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/menu-tipusok" element={<AuthGuard><AdminGuard><MenuTypeList /></AdminGuard></AuthGuard>} />
            <Route path="/admin/etelek" element={<AuthGuard><AdminGuard><DishList /></AdminGuard></AuthGuard>} />
            <Route path="/admin/etel-tipusok" element={<AuthGuard><AdminGuard><FoodTypeList /></AdminGuard></AuthGuard>} />
            <Route path="/admin/etterem-jellegek" element={<AuthGuard><AdminGuard><CuisineTypeList /></AdminGuard></AuthGuard>} />
            <Route path="/admin/utalvanyok" element={<AuthGuard><AdminGuard><VoucherTypeList /></AdminGuard></AuthGuard>} />
            <Route path="/admin/adathozzaferes" element={<AuthGuard><AdminOnlyGuard><DataAccess /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/felhasznalok" element={<AuthGuard><AdminOnlyGuard><UserList /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/hirlevel-cimlista" element={<AuthGuard><AdminOnlyGuard><NewsletterList /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/email-templates" element={<AuthGuard><AdminOnlyGuard><EmailTemplates /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/email-templates/:id" element={<AuthGuard><AdminOnlyGuard><EmailTemplateForm /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/kovetokodok" element={<AuthGuard><AdminOnlyGuard><TrackingCodes /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/utemezett-feladatok" element={<AuthGuard><AdminOnlyGuard><ScheduledTasks /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/utemezett-feladatok/uj" element={<AuthGuard><AdminOnlyGuard><ScheduledTaskForm /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/utemezett-feladatok/:id" element={<AuthGuard><AdminOnlyGuard><ScheduledTaskForm /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/tartalmi-elemek" element={<AuthGuard><AdminOnlyGuard><ContentElements /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/galeria" element={<AuthGuard><AdminOnlyGuard><Gallery /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/newsletters" element={<AuthGuard><AdminOnlyGuard><Newsletters /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/logs" element={<AuthGuard><AdminOnlyGuard><Logs /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/bannerek" element={<AuthGuard><AdminOnlyGuard><BannerList /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/popupok" element={<AuthGuard><AdminOnlyGuard><PopupList /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/szolgaltatasok" element={<AuthGuard><AdminOnlyGuard><ServicePackageList /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/backup" element={<AuthGuard><AdminOnlyGuard><Backup /></AdminOnlyGuard></AuthGuard>} />
            <Route path="/admin/megrendelesek" element={<AuthGuard><AdminGuard><OrderList /></AdminGuard></AuthGuard>} />
            {/* City slug route */}
            <Route path="/:citySlug" element={<Index />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
    </CartProvider>
  );
};

const App = () => (
  <HelmetProvider>
    <PersistQueryClientProvider 
      client={queryClient} 
      persistOptions={{ 
        persister,
        maxAge: 30 * 60 * 1000,
        buster: 'v3',
      }}
    >
      <AppContent />
    </PersistQueryClientProvider>
  </HelmetProvider>
);

export default App;
